import { Box, CircularProgress, Grid } from '@mui/material'
import { useContext, useEffect, useState } from 'react'
import { ConversionAddButton } from '../components/ConversionAddButton'
import { ConversionList } from '../components/ConversionList'
import { CustomExplain } from '../components/CustomExplain'
import { CustomPaper } from '../components/CustomPaper'
import { CustomTitle } from '../components/CustomTitile'
import { Footer } from '../components/Footer'
import { ModalConfirmDialog } from '../components/ModalConfirmDialog'
import { loadStatus } from '../constants/loadStatus'
import { useAuth } from '../hooks/useAuth'
import { useCB01ConversionGet } from '../hooks/useCB01ConversionGet'
import { useCB02ConversionAdd } from '../hooks/useCB02ConversionAdd'
import { useCB03ConversionDelete } from '../hooks/useCB03ConversionDelete'
import { useCB04ConversionUpdate } from '../hooks/useCB04ConversionUpdate'
import type { ConversionInfo } from '../interfaces/ConversionIf'
import { LanguageContext } from '../util/LanguageContext'
import { SideMenu } from './SideMenu'
import { useDeviceCapability } from '../util/DeviceCapabilityContext'

// 強制単語画面
export const Conversion = () => {
  // hooks
  const auth = useAuth()
  const cb01 = useCB01ConversionGet()
  const cb02 = useCB02ConversionAdd()
  const cb03 = useCB03ConversionDelete()
  const cb04 = useCB04ConversionUpdate()
  // 文字列変換用
  const translation = useContext(LanguageContext)
  // ダイアログ
  const [openDialog, setOpenDialog] = useState(false)
  const [errorDialog, setErrorDialog] = useState('')
  // 強制単語配列
  const [Conversions, setConversions] = useState<ConversionInfo[]>([])

  const {supportsOverwrite} = useDeviceCapability()

  // ローディングステータス監視
  useEffect(() => {
    if (
      cb01.loading === loadStatus.LOADING ||
      cb02.loading === loadStatus.LOADING ||
      cb03.loading === loadStatus.LOADING ||
      cb04.loading === loadStatus.LOADING
    ) {
      auth.setIsDisable(true)
    } else {
      auth.setIsDisable(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cb01.loading, cb02.loading, cb03.loading, cb04.loading])

  // 強制単語追加ハンドラ
  const ConversionAddHandler = async (inputPreTextValue: string, inputpostTextValue: string) => {
    const result = await cb02.execConversionAdd(
      auth.deviceId,
      inputPreTextValue,
      inputpostTextValue,
      { overwrite: supportsOverwrite },
    )
    if (result.success) {
      const result = await cb01.execConversionGet(auth.deviceId)
      if (result.success) {
        if (result.info) {
          setConversions(result.info!)
        }
      } else {
        setErrorDialog(result.message!)
        setOpenDialog(true)
      }
    } else {
      setErrorDialog(result.message!)
      setOpenDialog(true)
    }
  }
  // 強制単語削除ハンドラ
  const ConversionDelHandler = async (inputValue: string) => {
    const result = await cb03.execConversionDelete(auth.deviceId, inputValue)
    if (result.success) {
      const result = await cb01.execConversionGet(auth.deviceId)
      if (result.success) {
        if (result.info) {
          setConversions(result.info!)
        } else {
          setConversions([])
        }
      } else {
        setErrorDialog(result.message!)
        setOpenDialog(true)
      }
    } else {
      setErrorDialog(result.message!)
      setOpenDialog(true)
    }
  }

  // 強制単語更新ハンドラ
  const ConversionUpdateHandler = async (oldPreText: string, newPreText: string, newPostText: string) => {
    const result = await cb04.execConversionUpdate(auth.deviceId, oldPreText, newPreText, newPostText)
    if (result.success) {
      const r2 = await cb01.execConversionGet(auth.deviceId)
      if (r2.success) {
        if (r2.info) setConversions(r2.info!)
        else setConversions([])
      } else {
        setErrorDialog(r2.message ?? '')
        setOpenDialog(true)
      }
    } else {
      setErrorDialog(result.message ?? '')
      setOpenDialog(true)
    }
  }


  // 起動時
  useEffect(() => {
    const fetchData = async () => {
      const result = await cb01.execConversionGet(auth.deviceId)
      if (result.success) {
        if (result.info) {
          setConversions(result.info!)
        }
      } else {
        setErrorDialog(result.message!)
        setOpenDialog(true)
      }
    }

    fetchData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // ダイアログクローズ
  const handleDialogClose = () => {
    setErrorDialog('')
    setOpenDialog(false)
  }

  return (
    <>
      {openDialog && (
        <ModalConfirmDialog
          open={openDialog}
          text={errorDialog}
          onClick={() => handleDialogClose()}
        />
      )}
      <Box sx={{ display: 'flex', p: 0 }}>
        <Box sx={{ p: 0, width: '338px', flexShrink: 0 }}>
          <SideMenu />
        </Box>
        <Box sx={{ flexGrow: 1, overflowX: 'auto' }} display="flex" flexDirection="column">
          <Box marginTop={13}>
            <CustomTitle>{translation['label_forceword']}</CustomTitle>
          </Box>
          <Box
            sx={{
              position: 'fixed',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              zIndex: 9999,
            }}
          >
            {(cb01.loading === loadStatus.LOADING ||
              cb02.loading === loadStatus.LOADING ||
              cb03.loading === loadStatus.LOADING) && <CircularProgress />}
          </Box>
          <Box>
            <CustomExplain>{translation['label_forceword_info']}</CustomExplain>
          </Box>
          <Box
            sx={{
              marginTop: '40px',
            }}
          >
            <CustomPaper>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <ConversionAddButton
                    title={translation['label_forceword']}
                    conversions={Conversions}
                    onAdd={ConversionAddHandler}
                    overwrite={supportsOverwrite}
                  >
                    {translation['button_add_new']}
                  </ConversionAddButton>
                </Grid>
                <ConversionList items={Conversions} onDeleteConversion={ConversionDelHandler} onUpdateConversion={ConversionUpdateHandler}/>
              </Grid>
            </CustomPaper>
          </Box>
        </Box>
      </Box>
      <Footer />
    </>
  )
}